/**
 * Anchor Browser (anchorbrowser.io) - typed shapes for the session REST API.
 *
 * Anchor runs a hosted stealth Chromium. Agents drive it via the hosted MCP at
 * `https://api.anchorbrowser.io/mcp` (auth: `anchor-api-key` header). A
 * profile-bound, authenticated session is minted out-of-band via this REST API
 * (base `https://api.anchorbrowser.io/v1`, same `anchor-api-key` header) and the
 * returned session id is fed to the MCP as the `anchor-session-id` header
 * (ENG-5857 / ENG-7748). See `integrations/registry.ts` for the MCP wiring.
 *
 * VERIFICATION STATUS (ENG-7748). The request/response shapes below are the
 * best-known contract from the ENG-5854 spike harness (`docs/spikes/
 * eng-5854-anchor-browser/lib.mjs`, which mints/ends sessions against the live
 * API) plus the ENG-5857 scoping comments. Anchor's public docs 403 to fetchers,
 * so a few fields could NOT be pinned in-repo and are marked `// anchor-verify`:
 *   - the exact JSON path of the session id in the create response,
 *   - the placement of `dedicated_sticky_ip` in the create body,
 *   - whether/how `max_duration` / `idle_timeout` are request vs response fields.
 * ENG-7749 confirms these live against a real account before anchor-browser
 * persistent sessions leave `beta`. The client is written so adjusting a field
 * here (or the two body/extract helpers) is a localized change - the manager
 * computes expiry from the max-duration it REQUESTS, so it never depends on the
 * response reporting expiry.
 */

/** Default Anchor REST API base url. Overridable via `AnchorClientOptions.baseUrl`. */
export const ANCHOR_API_BASE_URL = 'https://api.anchorbrowser.io/v1';

/** Credential header Anchor authenticates on (NOT `Authorization: Bearer`). */
export const ANCHOR_API_KEY_HEADER = 'anchor-api-key';

/** Header that binds an MCP connection to a minted, profile-authenticated session. */
export const ANCHOR_SESSION_ID_HEADER = 'anchor-session-id';

/**
 * Input to {@link AnchorSessionClient.createSession}. All fields optional: with
 * no `profileName` the session is a fresh, unauthenticated (stateless) browser -
 * the current default behaviour when no profile is configured.
 */
export interface CreateAnchorSessionInput {
  /**
   * Name of a persistent Anchor profile to bind (the authenticated cookies/
   * tokens captured during onboarding). Omit for a stateless ephemeral session.
   */
  profileName?: string;
  /**
   * Persist the session's state back into the profile when it ends. FALSE for
   * runtime reuse (we only READ the profile); the persist-on-end flow belongs to
   * the one-time onboarding (ENG-7749).
   */
  persist?: boolean;
  /**
   * Request a dedicated sticky egress IP for the session (IP stability for
   * bot-sensitive targets like LinkedIn). `dedicated_sticky_ip` is a real Anchor
   * flag (the API 400s on unknown keys). // anchor-verify: exact body placement.
   */
  dedicatedStickyIp?: boolean;
  /**
   * Max session lifetime in minutes. The manager sets this so it OWNS the expiry
   * clock (re-mints before it elapses) rather than trusting the response.
   * // anchor-verify: request field name/placement + units.
   */
  maxDurationMinutes?: number;
  /**
   * Idle timeout in minutes - Anchor ends the session after this much inactivity.
   * // anchor-verify: request field name/placement + units.
   */
  idleTimeoutMinutes?: number;
}

/** A minted Anchor session. `raw` is the untouched response for diagnostics. */
export interface AnchorSession {
  /** The session id to send as the `anchor-session-id` MCP header. */
  sessionId: string;
  /**
   * The live-view URL a human opens to drive the browser by hand (the one-time
   * onboarding login). Present when the response carries it. // anchor-verify
   */
  liveViewUrl?: string;
  /** The full parsed create response (shape not pinned - see VERIFICATION STATUS). */
  raw: unknown;
}
